import { useEffect } from 'react'

import { useEventCallback } from './use-event-callback'

/**
 * Global keyboard map.
 *
 * Generalizes `admin/utils/useShortcuts.ts`, which only ever served the pack editor.
 *
 * Bindings look like `mod+k`, `shift+?`, `escape`, or a bare `/`. `mod` is Cmd on
 * macOS and Ctrl elsewhere.
 *
 * Bare-key bindings are suppressed while the operator is typing in a field, which
 * is the difference between `/` focusing search and `/` appearing in the middle of
 * someone's search term. Modifier combinations and Escape still fire, because those
 * are the ones people expect to work from inside a form.
 */

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    if (target.isContentEditable) return true
    const tag = target.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    // CodeMirror and other custom editors report themselves via role.
    return target.closest('[role="textbox"], .cm-editor') !== null
}

interface ParsedBinding {
    key: string
    mod: boolean
    shift: boolean
    alt: boolean
}

function parseBinding(binding: string): ParsedBinding {
    const parts = binding.toLowerCase().split('+')
    const key = parts[parts.length - 1] ?? ''
    return {
        key,
        mod: parts.includes('mod') || parts.includes('cmd') || parts.includes('ctrl'),
        shift: parts.includes('shift'),
        alt: parts.includes('alt') || parts.includes('option')
    }
}

function matches(event: KeyboardEvent, binding: ParsedBinding): boolean {
    const pressedMod = isMac ? event.metaKey : event.ctrlKey
    if (binding.mod !== pressedMod) return false
    if (binding.alt !== event.altKey) return false

    const eventKey = event.key.toLowerCase()
    // `shift` is implicit for characters that require it (`?`, `:`), so only enforce
    // it when the binding names a key that shift does not itself produce.
    if (binding.shift && !event.shiftKey) return false
    if (!binding.shift && event.shiftKey && binding.key.length === 1 && /[a-z0-9]/.test(binding.key)) return false

    return eventKey === binding.key
}

export interface HotkeyOptions {
    /** Fire even while a field has focus. Defaults to true for modifier combos and Escape. */
    enableOnFormTags?: boolean
    enabled?: boolean
    preventDefault?: boolean
}

export function useHotkey(binding: string, handler: (event: KeyboardEvent) => void, options: HotkeyOptions = {}) {
    const { enabled = true, preventDefault = true } = options
    const onHotkey = useEventCallback(handler)

    useEffect(() => {
        if (!enabled) return

        const parsed = parseBinding(binding)
        const allowWhileTyping = options.enableOnFormTags ?? (parsed.mod || parsed.key === 'escape')

        const onKeyDown = (event: KeyboardEvent) => {
            if (!matches(event, parsed)) return
            if (!allowWhileTyping && isTypingTarget(event.target)) return
            if (preventDefault) event.preventDefault()
            onHotkey(event)
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [binding, enabled, onHotkey, preventDefault, options.enableOnFormTags])
}

/** Display string for a binding, so on-screen hints match the platform. */
export function formatBinding(binding: string): string {
    return binding
        .split('+')
        .map(part => {
            const key = part.toLowerCase()
            if (key === 'mod') return isMac ? '\u2318' : 'Ctrl'
            if (key === 'shift') return isMac ? '\u21e7' : 'Shift'
            if (key === 'alt') return isMac ? '\u2325' : 'Alt'
            if (key === 'enter') return isMac ? '\u21a9' : 'Enter'
            if (key === 'escape') return 'Esc'
            return key.length === 1 ? key.toUpperCase() : key
        })
        .join(isMac ? '' : '+')
}

export { isMac }
